import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSmile,
  faPaperclip,
  faMicrophone,
  faTrashAlt,
  faPaperPlane,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import SidebarLayout from "../components/sidebar/SidebarLayout";
import DeleteMessageConfirmModal from "../components/whatsapp/DeleteMessageConfirmModal";
import GroupListRows from "../components/whatsappGroups/GroupListRows";
import GroupDetailPanel from "../components/whatsappGroups/GroupDetailPanel";
import CreateGroupModal from "../components/whatsappGroups/CreateGroupModal";
import useWhatsappGroups from "../hooks/useWhatsappGroups";
import Card from "../components/layout/Card";
import Button from "../components/layout/Button";
import Modal from "../components/layout/Modal";
import Avatar from "../components/layout/Avatar";
import { useAuth } from "../services/firebase/auth";
import { useWhatsappNotifications } from "../services/whatsappNotifications";
import { getQueues, getExtensions } from "../model/queue.model";
import { phoneMask } from "../utils/masks";
import {
  WhatsappContact,
  WhatsappConversation,
  WhatsappInstance,
  WhatsappMessage,
  assignConversation,
  closeConversation,
  markConversationRead,
  createContactForConversation,
  deleteContact,
  deleteMessage,
  phoneLookupVariants,
  removeReaction,
  sendMedia,
  sendMessage,
  sendReaction,
  startConversation,
  transferConversation,
  uploadWhatsappMedia,
  watchContacts,
  watchConversations,
  createConversationThread,
  watchInstance,
  isConversationVisibleToAgent,
  computeMyQueueIds,
} from "../model/whatsapp.model";
import {
  formatMessageTime,
  formatListTime,
  formatPhoneDisplay,
  formatDayDivider,
  shouldShowDayDivider,
} from "../components/whatsapp/format";

const MAX_ATTACHMENT_MB = 20;

// Mesmo conjunto "rápido" que o WhatsApp mostra ao segurar uma mensagem —
// cobre o uso comum sem reabrir o seletor de 48 emojis pra isso.
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Lista curada em vez de uma lib nova — cobre o uso comum de atendimento
// sem depender de instalar nada (o ambiente já teve atrito com deps nativas).
const EMOJIS = [
  "😀", "😁", "😂", "🙂", "😉", "😊", "😍", "🤔",
  "😅", "😢", "😭", "😡", "😱", "😴", "🤗", "🥳",
  "😎", "🤷", "🙋", "👍", "👎", "🙏", "👏", "💪",
  "🤝", "✋", "👌", "🤞", "👋", "❤️", "🔥", "🎉",
  "⭐", "💯", "✅", "❌", "⚠️", "ℹ️", "✔️", "📌",
  "📅", "⏰", "📎", "📷", "🎧", "📄", "💵", "📞",
];

// Ícones de confirmação de entrega, no mesmo padrão do WhatsApp: 1 check
// cinza (enviada), 2 checks cinza (entregue), 2 checks azul (lida). Só faz
// sentido em mensagens que nós mandamos — o WhatsApp nunca mostra isso em
// mensagens recebidas.
function MessageTicks(props: { status?: WhatsappMessage["status"] }) {
  if (!props.status) return null;
  if (props.status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 flex-none text-red"
        title="Não foi possível entregar esta mensagem"
      >
        <svg
          viewBox="0 0 16 16"
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        >
          <circle cx="8" cy="8" r="6.5" />
          <line x1="8" y1="5" x2="8" y2="8.5" strokeLinecap="round" />
          <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
        </svg>
        <span className="text-[10px] font-semibold">Não entregue</span>
      </span>
    );
  }
  const Check = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 16 12"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 6.5L5 10.5L15 1" />
    </svg>
  );
  // Azul do "lido" nativo do WhatsApp (#53bdeb) — precisa ser bem distinto
  // do cinza claro do "entregue", não uma variação sutil dele.
  const color = props.status === "read" ? "text-[#53bdeb]" : "text-white/60";
  return (
    <span className={`inline-flex items-center flex-none ${color}`}>
      <Check className="w-3.5 h-3.5" />
      {props.status !== "sent" && <Check className="w-3.5 h-3.5 -ml-2.5" />}
    </span>
  );
}

type Tab = "unassigned" | "mine" | "all" | "everyone" | "groups";
type Queue = {
  id: string;
  name: string;
  option: number;
  participants?: { id?: string }[];
};
type Extension = { value: string; name: string };

function WhatsappPage() {
  const auth = useAuth();
  const companyId = auth.user.token.claims.company;
  const myId = auth.user.uid;
  const isAdmin = !!auth.user.token.claims.admin;
  const {
    pendingConversationId,
    clearPendingConversationId,
    pendingGroupId,
    clearPendingGroupId,
  } = useWhatsappNotifications();

  const [conversations, setConversations] = useState<WhatsappConversation[]>([]);
  const [contacts, setContacts] = useState<WhatsappContact[]>([]);
  const [instance, setInstance] = useState<WhatsappInstance | null>(null);
  // Sem isso, "instance === null" não dá pra distinguir "ainda carregando"
  // de "empresa nunca configurada" — os dois começam iguais.
  const [instanceLoaded, setInstanceLoaded] = useState(false);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);

  // Estado/lógica da aba "Grupos" isolados num hook à parte, de propósito
  // (ver useWhatsappGroups) — evita transformar `selected` (conversa) numa
  // união de tipos que forçaria checagem condicional nos ~15 pontos que já
  // leem selected.assignedAgentId/selected.queueId/etc.
  const groupsHook = useWhatsappGroups(companyId, myId, extensions);

  const [tab, setTab] = useState<Tab>("unassigned");
  const [search, setSearch] = useState("");
  const [queueFilter, setQueueFilter] = useState("");
  const [queueFilterOpen, setQueueFilterOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  const [assuming, setAssuming] = useState(false);
  const [closingConversation, setClosingConversation] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSearch, setTransferSearch] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState<WhatsappMessage | null>(null);
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  const [deleteConfirmFor, setDeleteConfirmFor] = useState<WhatsappMessage | null>(null);
  const [deletingMessage, setDeletingMessage] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordCancelledRef = useRef(false);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);

  // Abre sempre na mensagem mais recente (comportamento padrão de qualquer
  // app de chat) — sem isso o scroll ficava no topo, na mensagem mais
  // antiga do histórico. `shouldStickToBottomRef` evita puxar o scroll pra
  // baixo à força quando o agente está lendo histórico antigo e chega uma
  // mensagem nova nesse meio tempo.
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);

  // Histórico é paginado (ver createConversationThread) — carrega mais
  // automaticamente quando o agente rola perto do topo, preservando a
  // posição visual do scroll (sem isso, prepender mensagens mais antigas
  // faz a tela "pular").
  const threadRef = useRef<{ loadMore: () => Promise<void>; dispose: () => void } | null>(
    null,
  );
  const loadingMoreRef = useRef(false);
  const pendingScrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(
    null,
  );
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  const loadMoreMessages = async () => {
    if (loadingMoreRef.current || !threadRef.current) return;
    const el = messagesContainerRef.current;
    loadingMoreRef.current = true;
    setLoadingMoreMessages(true);
    if (el) {
      pendingScrollRestoreRef.current = {
        scrollHeight: el.scrollHeight,
        scrollTop: el.scrollTop,
      };
    }
    await threadRef.current.loadMore();
    loadingMoreRef.current = false;
    setLoadingMoreMessages(false);
  };

  const onMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    const unsubs = [
      watchConversations(companyId, setConversations),
      watchContacts(companyId, setContacts),
      watchInstance(companyId, (i) => {
        setInstance(i);
        setInstanceLoaded(true);
      }),
    ];
    getQueues(auth).then(setQueues);
    getExtensions(auth).then(setExtensions);
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Clique numa notificação de mensagem nova (WhatsappNotificationsProvider,
  // montado acima desta página em App.tsx) — roda tanto ao entrar na rota
  // quanto com a página já aberta (troca de rota não remonta quem já está
  // em /whatsapp).
  useEffect(() => {
    if (!pendingConversationId) return;
    setSelectedId(pendingConversationId);
    clearPendingConversationId();
  }, [pendingConversationId, clearPendingConversationId]);

  // Mesma coisa pra clique em notificação de mensagem de GRUPO — troca pra
  // aba certa (grupo vive numa aba separada de conversa 1:1) antes de
  // selecionar.
  useEffect(() => {
    if (!pendingGroupId) return;
    setTab("groups");
    groupsHook.setSelectedId(pendingGroupId);
    clearPendingGroupId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGroupId, clearPendingGroupId]);

  const selected = conversations.find((c) => c.id === selectedId) || null;

  useEffect(() => {
    if (!selected) {
      setMessages([]);
      setHasMoreMessages(false);
      threadRef.current = null;
      return;
    }
    setReplyTo(null);
    shouldStickToBottomRef.current = true;
    pendingScrollRestoreRef.current = null;
    const thread = createConversationThread(
      companyId,
      selected.contactPhone,
      selected.id,
      (list, hasMore) => {
        setMessages(list);
        setHasMoreMessages(hasMore);
      },
    );
    threadRef.current = thread;
    return () => {
      thread.dispose();
      threadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selected?.id, selected?.contactPhone]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (pendingScrollRestoreRef.current) {
      const { scrollHeight, scrollTop } = pendingScrollRestoreRef.current;
      pendingScrollRestoreRef.current = null;
      el.scrollTop = el.scrollHeight - scrollHeight + scrollTop;
      return;
    }
    if (shouldStickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const messageById = useMemo(() => {
    const map: Record<string, WhatsappMessage> = {};
    messages.forEach((m) => (map[m.id] = m));
    return map;
  }, [messages]);

  // Solta o microfone se a página for fechada/trocada com uma gravação em
  // andamento — sem isso o indicador de mic do navegador fica ligado.
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        recordCancelledRef.current = true;
        mediaRecorderRef.current.stop();
      }
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
  }, []);

  const queueById = useMemo(() => {
    const map: Record<string, Queue> = {};
    queues.forEach((q) => (map[q.id] = q));
    return map;
  }, [queues]);

  // Um agente só vê conversas de filas em que participa — mesma regra de
  // acesso das filas de chamada, não um controle novo (ver plano). Conversas
  // já atribuídas a mim continuam visíveis mesmo se eu não participar mais
  // da fila atual (ex: acabei de ser tirado dela).
  const myQueueIds = useMemo(
    () => computeMyQueueIds(queues, myId),
    [queues, myId],
  );

  // "Todas" mostra só o que é meu ou está sem dono numa fila em que
  // participo — conversa atribuída a OUTRO atendente não aparece mais aqui,
  // nem pra quem transferiu (pedido explícito do usuário: depois de
  // transferir, a conversa some da própria lista, mesmo continuando na
  // mesma fila). Mesma função usada em whatsappNotifications.tsx — não
  // duplique essa regra de novo (ver comentário em model/whatsapp.model.ts).
  const isVisibleToMe = (c: WhatsappConversation) =>
    isConversationVisibleToAgent(c, myId, myQueueIds);

  // Se a conversa aberta na tela deixar de ser minha (transferida pra
  // outro atendente enquanto eu estava com ela aberta), fecha o painel —
  // sem isso ela some da lista mas continua exibida em detalhe. Admin não
  // é afetado: pode ficar olhando qualquer conversa via a aba "Todos os
  // atendimentos", que não segue essa regra de visibilidade.
  useEffect(() => {
    if (selected && !isVisibleToMe(selected) && !isAdmin) setSelectedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, myQueueIds, myId, isAdmin]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // "everyone" só existe pra admin e ignora o escopo por fila/atribuição
    // de propósito — é a aba separada da "Todas" pedida explicitamente
    // (mostrar tudo da empresa sem misturar com a visão normal do agente).
    const base =
      tab === "everyone" && isAdmin ? conversations : conversations.filter(isVisibleToMe);
    return base
      .filter((c) => {
        if (tab === "unassigned") return !c.assignedAgentId;
        if (tab === "mine") return c.assignedAgentId === myId;
        return true;
      })
      .filter(
        (c) =>
          !q ||
          c.contactName.toLowerCase().includes(q) ||
          c.contactPhone.includes(q),
      )
      .filter((c) => !queueFilter || c.queueId === queueFilter)
      .sort((a, b) => b.lastMessageAt - a.lastMessageAt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, tab, search, queueFilter, myId, myQueueIds, isAdmin]);

  const visibleConversations = conversations.filter(isVisibleToMe);
  const unassignedCount = visibleConversations.filter(
    (c) => !c.assignedAgentId,
  ).length;
  const mineCount = visibleConversations.filter(
    (c) => c.assignedAgentId === myId,
  ).length;
  const groupsUnreadCount = groupsHook.groups.reduce(
    (sum, g) => sum + (g.unreadByUser?.[myId] || 0),
    0,
  );

  const isUnassigned = !!selected && !selected.assignedAgentId;
  const isMine = !!selected && selected.assignedAgentId === myId;
  const isOther = !!selected && !!selected.assignedAgentId && !isMine;
  const isKnownContact =
    !!selected &&
    (() => {
      const variants = new Set(phoneLookupVariants(selected.contactPhone));
      return contacts.some((c) => variants.has(c.phone));
    })();

  // Exclui eu mesmo e quem já está com a conversa (não faz sentido
  // "transferir" pra quem já é o dono — cobre o caso do admin abrindo o
  // menu numa conversa de outro atendente).
  const otherExtensions = extensions.filter(
    (e) => e.value !== myId && e.value !== selected?.assignedAgentId,
  );

  const onAssume = async () => {
    if (!selected || assuming) return;
    setAssuming(true);
    const result = await assignConversation({ conversationId: selected.id });
    if (result.error) toast.error("Não foi possível assumir a conversa.");
    setAssuming(false);
  };

  const onClose = async () => {
    if (!selected || closingConversation) return;
    setClosingConversation(true);
    const result = await closeConversation({ conversationId: selected.id });
    if (result.error) {
      toast.error("Não foi possível encerrar a conversa.");
    } else {
      setSelectedId(null);
    }
    // Bug real corrigido (2026-09-15): faltava resetar aqui no caminho de
    // sucesso (só resetava no erro) — closingConversation é estado
    // compartilhado (não por conversa), então depois de encerrar com
    // sucesso o botão "Encerrar" da PRÓXIMA conversa selecionada nascia
    // preso em loading, só saindo com F5. Mesmo padrão já usado em
    // onAssume/onTransferToQueue/onTransferToAgent (reset incondicional).
    setClosingConversation(false);
  };

  // Ação manual — não fecha nem desatribui a conversa, só zera o contador
  // de não lida (pedido do usuário: cliente respondeu algo tipo "ok, vou
  // analisar" que não precisa de reply, mas o atendente já viu). Mesmo
  // reset incondicional de propósito (ver comentário no onClose acima).
  const onMarkRead = async () => {
    if (!selected || markingRead) return;
    setMarkingRead(true);
    const result = await markConversationRead({ conversationId: selected.id });
    if (result.error) toast.error("Não foi possível marcar como lida.");
    setMarkingRead(false);
  };

  const onTransferToQueue = async (targetQueueId: string) => {
    if (!selected || transferring) return;
    setTransferOpen(false);
    setTransferSearch("");
    setTransferring(true);
    const result = await transferConversation({
      conversationId: selected.id,
      targetQueueId,
    });
    if (result.error) toast.error("Não foi possível transferir a conversa.");
    setTransferring(false);
  };

  const onTransferToAgent = async (targetAgentId: string) => {
    if (!selected || transferring) return;
    setTransferOpen(false);
    setTransferSearch("");
    setTransferring(true);
    const result = await transferConversation({
      conversationId: selected.id,
      targetAgentId,
    });
    if (result.error) toast.error("Não foi possível transferir a conversa.");
    setTransferring(false);
  };

  const insertEmoji = (emoji: string) => {
    const el = composerRef.current;
    const start = el?.selectionStart ?? composerText.length;
    const end = el?.selectionEnd ?? composerText.length;
    const next = composerText.slice(0, start) + emoji + composerText.slice(end);
    setComposerText(next);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + emoji.length;
      el?.setSelectionRange(pos, pos);
    });
  };

  // Cresce verticalmente conforme o texto quebra linha (até um teto, depois
  // rola por dentro) — sem isso um <textarea> controlado fica preso na
  // altura de 1 linha. Reseta pra "auto" antes de medir, senão scrollHeight
  // nunca encolhe de volta ao apagar texto.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [composerText]);

  const onSend = async () => {
    if (!selected || !composerText.trim() || sending) return;
    setSending(true);
    const text = composerText.trim();
    const replyToMessageId = replyTo?.id;
    setComposerText("");
    setReplyTo(null);
    const result = await sendMessage({
      conversationId: selected.id,
      text,
      replyToMessageId,
    });
    if (result.error) toast.error("Não foi possível enviar a mensagem.");
    setSending(false);
  };

  const onReact = async (m: WhatsappMessage, emoji: string) => {
    if (!selected) return;
    setReactionPickerFor(null);
    const result =
      m.reactions?.out === emoji
        ? await removeReaction({ conversationId: selected.id, messageId: m.id })
        : await sendReaction({ conversationId: selected.id, messageId: m.id, emoji });
    if (result.error) toast.error("Não foi possível reagir à mensagem.");
  };

  const onDeleteMessage = async () => {
    if (!selected || !deleteConfirmFor || deletingMessage) return;
    setDeletingMessage(true);
    try {
      const result = await deleteMessage({
        conversationId: selected.id,
        messageId: deleteConfirmFor.id,
      });
      if (result.error) toast.error("Não foi possível apagar a mensagem.");
    } finally {
      setDeletingMessage(false);
      setDeleteConfirmFor(null);
    }
  };

  const onAttachFile = async (file: File) => {
    if (!selected || uploading) return;
    if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
      toast.error(`Arquivo maior que ${MAX_ATTACHMENT_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await uploadWhatsappMedia(companyId, selected.id, file);
      const result = await sendMedia({ conversationId: selected.id, ...uploaded });
      if (result.error) toast.error("Não foi possível enviar o anexo.");
    } catch (e) {
      toast.error("Falha ao enviar o arquivo.");
    }
    setUploading(false);
  };

  const startRecording = async () => {
    if (!selected || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Chrome/Firefox/Edge gravam opus em webm por padrão; Safari não
      // suporta webm e cai pro mp4 — a Z-API aceita base64 sem exigir um
      // codec específico (doc mostra até mp3 por link), então manda como
      // gravado, sem transcodificar.
      const mimeType = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
      ].find((c) => window.MediaRecorder?.isTypeSupported?.(c));
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recordedChunksRef.current = [];
      recordCancelledRef.current = false;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (!recordCancelledRef.current && recordedChunksRef.current.length > 0) {
          const type = recorder.mimeType || "audio/webm";
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          const blob = new Blob(recordedChunksRef.current, { type });
          onAttachFile(new File([blob], `audio-${Date.now()}.${ext}`, { type }));
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopRecording = (cancel: boolean) => {
    recordCancelledRef.current = cancel;
    mediaRecorderRef.current?.stop();
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    setRecording(false);
  };

  const notProvisioned = instanceLoaded && !instance;

  return (
    <SidebarLayout fullWidth>
      <div className="flex items-end justify-between mb-2">
        <h1 className="text-primary font-bold text-lg 2xl:text-2xl">WhatsApp</h1>
      </div>
      <hr className="border-t-gray border-t-2 mb-4"></hr>

      {notProvisioned && (
        <Card className="flex flex-col items-center justify-center text-center gap-3 py-16 px-8">
          <div className="text-3xl">💬</div>
          <div className="font-bold text-primary">
            O WhatsApp ainda não foi configurado para a sua empresa
          </div>
          <p className="text-xs text-gray-dark max-w-[420px]">
            Fale com o administrador da sua conta ou com o suporte Voki para
            habilitar esse canal.
          </p>
        </Card>
      )}

      {!notProvisioned && (
      <div className="flex gap-5 h-[calc(100vh-160px)]">
        {/* ---- lista ---- */}
        <Card className="w-[360px] flex-none flex flex-col p-0 2xl:p-0 overflow-hidden">
          <div className="p-4 pb-2 flex gap-2 flex-none">
            <Field
              placeholder={tab === "groups" ? "Buscar grupo" : "Buscar conversa ou telefone"}
              value={tab === "groups" ? groupsHook.search : search}
              setter={tab === "groups" ? groupsHook.setSearch : setSearch}
              autocomplete
              className="flex-1"
            />
            <button
              onClick={() =>
                tab === "groups" ? groupsHook.setShowCreateModal(true) : setShowNewConvModal(true)
              }
              title={tab === "groups" ? "Novo grupo" : "Nova conversa"}
              className="w-9 h-9 rounded-10 bg-purple/10 text-purple flex-none grid place-items-center font-bold text-lg"
            >
              +
            </button>
          </div>

          <div className="flex gap-1.5 px-4 pb-3 flex-none flex-wrap">
            {(
              [
                { id: "unassigned", label: "Não atribuídas", count: unassignedCount },
                { id: "mine", label: "Minhas", count: mineCount },
                { id: "all", label: "Todas", count: visibleConversations.length },
                ...(isAdmin
                  ? [
                      {
                        id: "everyone" as Tab,
                        label: "Todos os atendimentos",
                        count: conversations.length,
                      },
                    ]
                  : []),
                // Diferente das outras abas (que contam conversas), aqui é
                // total de mensagens não lidas por mim, somadas de todos os
                // grupos — não tem "não atribuída"/fila/workload pra grupo.
                {
                  id: "groups" as Tab,
                  label: "Grupos",
                  count: groupsUnreadCount > 0 ? groupsUnreadCount : undefined,
                },
              ] as { id: Tab; label: string; count?: number }[]
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 py-1.5 px-3 rounded-10 text-xs font-bold ${
                  tab === t.id ? "bg-purple/10 text-purple" : "text-gray-dark"
                }`}
              >
                {t.label}{" "}
                {t.count !== undefined && <span className="opacity-70">{t.count}</span>}
              </button>
            ))}

            {tab !== "groups" && (
              <div className="relative">
                <button
                  onClick={() => setQueueFilterOpen((v) => !v)}
                  className={`flex items-center gap-1.5 py-1.5 px-3 rounded-10 text-xs font-bold ${
                    queueFilter ? "bg-purple/10 text-purple" : "text-gray-dark"
                  }`}
                >
                  {queueFilter ? queueById[queueFilter]?.name || "Fila" : "Fila"} ▾
                </button>
                {queueFilterOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setQueueFilterOpen(false)}
                    ></div>
                    <div className="absolute left-0 top-full mt-1 w-[200px] max-h-[260px] overflow-y-auto bg-white rounded-15 drop-shadow-far p-2 z-50">
                      <button
                        onClick={() => {
                          setQueueFilter("");
                          setQueueFilterOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-10 text-xs font-bold ${
                          !queueFilter ? "bg-purple/10 text-purple" : "text-gray-dark hover:bg-white-dark"
                        }`}
                      >
                        Todas as filas
                      </button>
                      {queues.map((q) => (
                        <button
                          key={q.id}
                          onClick={() => {
                            setQueueFilter(q.id);
                            setQueueFilterOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-10 text-xs font-bold ${
                            queueFilter === q.id
                              ? "bg-purple/10 text-purple"
                              : "text-gray-dark hover:bg-white-dark"
                          }`}
                        >
                          {q.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {tab === "groups" && (
            <div className="flex-1 overflow-y-auto px-2 pb-3">
              <GroupListRows hook={groupsHook} myId={myId} />
            </div>
          )}

          {tab !== "groups" && (
          <div className="flex-1 overflow-y-auto px-2 pb-3">
            {rows.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left flex gap-2.5 p-2 rounded-15 mb-0.5 ${
                  c.id === selectedId ? "bg-purple/10" : "hover:bg-white-dark"
                }`}
              >
                <Avatar avatar="" className="bg-gray-bluish w-9 h-9 flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="font-bold text-xs text-primary truncate">
                      {c.contactName}
                    </div>
                    <div className="flex items-center gap-1.5 flex-none">
                      {!!c.lastMessageAt && (
                        <span className="text-[9.5px] text-gray-dark tabular-nums">
                          {formatListTime(c.lastMessageAt)}
                        </span>
                      )}
                      {c.unreadCount > 0 && (
                        <div className="bg-purple text-white text-[10px] font-bold rounded-full min-w-[17px] h-[17px] grid place-items-center px-1">
                          {c.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-dark">
                    {formatPhoneDisplay(c.contactPhone)}
                  </div>
                  {!!c.lastMessageText && (
                    <div className="text-[10.5px] text-gray-dark truncate mt-0.5">
                      {c.lastMessageText}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span
                      className={`text-[9.5px] font-bold rounded-15 px-2 py-0.5 ${
                        c.botState?.active
                          ? "bg-purple/10 text-purple"
                          : "bg-white-dark text-black"
                      }`}
                    >
                      {c.botState?.active ? "Bot" : queueById[c.queueId]?.name || "Fila"}
                    </span>
                    {!c.assignedAgentId && (
                      <span className="text-[9.5px] font-bold border border-gray text-gray-dark rounded-15 px-2 py-0.5">
                        Não atribuída
                      </span>
                    )}
                    {tab === "everyone" && c.assignedAgentId && c.assignedAgentId !== myId && (
                      <span className="text-[9.5px] font-bold bg-purple/10 text-purple rounded-15 px-2 py-0.5">
                        {extensions.find((e) => e.value === c.assignedAgentId)?.name ||
                          "Outro atendente"}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
            {rows.length === 0 && (
              <div className="text-center text-xs text-gray-dark py-8">
                Nenhuma conversa aqui.
              </div>
            )}
          </div>
          )}
        </Card>

        {/* ---- conversa ---- */}
        <Card className="flex-1 flex flex-col p-0 2xl:p-0 overflow-hidden min-w-0">
          {tab === "groups" && (
            <GroupDetailPanel hook={groupsHook} contacts={contacts} extensions={extensions} />
          )}

          {tab !== "groups" && (
          <>
          {!selected && (
            <div className="flex-1 grid place-items-center text-gray-dark text-sm font-medium">
              Selecione uma conversa na lista
            </div>
          )}

          {selected && (
            <>
              <div className="flex items-center justify-between gap-3 py-4 px-5 border-b border-white-dark flex-none">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar avatar="" className="bg-gray-bluish w-10 h-10 flex-none" />
                  <div>
                    <div className="font-bold text-primary">{selected.contactName}</div>
                    <div className="text-xs text-gray-dark">
                      {formatPhoneDisplay(selected.contactPhone)}
                    </div>
                    <span
                      className={`inline-block mt-1 text-[9.5px] font-bold rounded-15 px-2 py-0.5 ${
                        selected.botState?.active
                          ? "bg-purple/10 text-purple"
                          : "bg-white-dark text-black"
                      }`}
                    >
                      {selected.botState?.active
                        ? "Bot"
                        : queueById[selected.queueId]?.name || "Fila"}
                    </span>
                  </div>
                </div>

                <div className="relative flex items-center gap-2 flex-none">
                  {!isKnownContact && (
                    <Button
                      outline
                      color="gray"
                      className="!text-xs"
                      onClick={() => setShowAddContactModal(true)}
                    >
                      Adicionar contato
                    </Button>
                  )}
                  {isUnassigned && (
                    <Button onClick={onAssume} className="!text-xs" isLoading={assuming}>
                      Assumir conversa
                    </Button>
                  )}
                  {isMine && (
                    <span className="text-xs font-bold text-green bg-green/10 rounded-15 py-2 px-3">
                      Atribuída a você
                    </span>
                  )}
                  {isOther && (
                    <span className="text-xs font-bold text-gray-dark bg-white-dark rounded-15 py-2 px-3">
                      Atribuída a{" "}
                      {extensions.find((e) => e.value === selected.assignedAgentId)
                        ?.name || "outro atendente"}
                    </span>
                  )}
                  {/* Admin pode transferir mesmo sem ser dono da conversa —
                      não pode encerrar nem responder, só mover pra outro
                      lugar (pedido explícito: ver/transferir, nada além disso). */}
                  {isMine && selected.unreadCount > 0 && (
                    <Button
                      outline
                      color="gray"
                      className="!text-xs"
                      onClick={onMarkRead}
                      isLoading={markingRead}
                    >
                      Marcar como lida
                    </Button>
                  )}
                  {(isMine || (isOther && isAdmin)) && (
                    <Button
                      outline
                      color="gray"
                      className="!text-xs"
                      onClick={() => setTransferOpen((v) => !v)}
                      isLoading={transferring}
                    >
                      Transferir
                    </Button>
                  )}
                  {isMine && (
                    <Button
                      outline
                      color="red"
                      className="!text-xs"
                      onClick={onClose}
                      isLoading={closingConversation}
                    >
                      Encerrar
                    </Button>
                  )}

                  {transferOpen &&
                    (() => {
                      const s = transferSearch.trim().toLowerCase();
                      const queueOptions = queues
                        .filter((q) => q.id !== selected.queueId)
                        .filter((q) => !s || q.name.toLowerCase().includes(s));
                      const agentOptions = otherExtensions.filter(
                        (e) => !s || e.name.toLowerCase().includes(s),
                      );
                      return (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => {
                              setTransferOpen(false);
                              setTransferSearch("");
                            }}
                          ></div>
                          <div className="absolute right-0 top-full mt-2 w-[260px] bg-white rounded-15 drop-shadow-far p-2.5 z-50">
                            <Field
                              placeholder="Buscar fila ou atendente"
                              value={transferSearch}
                              setter={setTransferSearch}
                              autocomplete
                              className="mb-1.5"
                            />
                            <div className="max-h-[260px] overflow-y-auto">
                              <div className="text-[10px] font-bold text-gray-dark uppercase tracking-wide px-2 pt-1 pb-1">
                                Transferir para fila
                              </div>
                              {queueOptions.map((q) => (
                                <button
                                  key={q.id}
                                  onClick={() => onTransferToQueue(q.id)}
                                  className="w-full text-left px-2 py-2 rounded-10 text-xs hover:bg-white-dark"
                                >
                                  {q.name}
                                </button>
                              ))}
                              {queueOptions.length === 0 && (
                                <div className="px-2 py-1.5 text-[11px] text-gray-dark">
                                  {s
                                    ? "Nenhuma fila encontrada."
                                    : "Nenhuma outra fila cadastrada."}
                                </div>
                              )}
                              <div className="text-[10px] font-bold text-gray-dark uppercase tracking-wide px-2 pt-2 pb-1">
                                Transferir para atendente
                              </div>
                              {agentOptions.map((e) => (
                                <button
                                  key={e.value}
                                  onClick={() => onTransferToAgent(e.value)}
                                  className="w-full text-left px-2 py-2 rounded-10 text-xs hover:bg-white-dark"
                                >
                                  {e.name}
                                </button>
                              ))}
                              {agentOptions.length === 0 && (
                                <div className="px-2 py-1.5 text-[11px] text-gray-dark">
                                  {s
                                    ? "Nenhum atendente encontrado."
                                    : "Nenhum outro atendente cadastrado."}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                </div>
              </div>

              <div
                ref={messagesContainerRef}
                onScroll={onMessagesScroll}
                className="flex-1 overflow-y-auto overflow-x-hidden p-5 flex flex-col gap-3 bg-white-mid"
              >
                {hasMoreMessages && (
                  <div className="text-center py-1">
                    {loadingMoreMessages ? (
                      <span className="text-[10.5px] text-gray-dark">
                        Carregando mensagens anteriores...
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={loadMoreMessages}
                        className="text-[10.5px] font-bold text-purple hover:underline"
                      >
                        Carregar mensagens anteriores
                      </button>
                    )}
                  </div>
                )}
                {messages.map((m, i) => {
                  // Separador de dia (pedido explícito do usuário,
                  // 2026-09-29) — sem isso, mensagem de ontem e de hoje
                  // ficavam grudadas sem nenhuma pista visual de virada de
                  // dia.
                  const dayDivider = shouldShowDayDivider(m.timestamp, messages[i - 1]?.timestamp) && (
                    <div className="flex justify-center my-1">
                      <span className="text-[11px] font-semibold text-gray-dark bg-white/80 rounded-full px-3 py-1 shadow-sm">
                        {formatDayDivider(m.timestamp)}
                      </span>
                    </div>
                  );

                  const replied = m.replyToMessageId
                    ? messageById[m.replyToMessageId]
                    : null;
                  const reactionChips = [m.reactions?.out, m.reactions?.in].filter(
                    Boolean,
                  ) as string[];
                  // v0: responder/reagir só funciona na conversa aberta atual —
                  // mensagens de conversas anteriores encerradas (mescladas na
                  // thread) ficam só-leitura pra essas duas ações.
                  const canAct = isMine && m.conversationId === selected.id;

                  const actions = canAct && !m.deleted && (
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pb-1 flex-none">
                      <button
                        onClick={() => setReactionPickerFor(m.id)}
                        title="Reagir"
                        className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
                      >
                        🙂
                      </button>
                      <button
                        onClick={() => setReplyTo(m)}
                        title="Responder"
                        className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
                      >
                        ↩
                      </button>
                      {m.direction === "out" && (
                        <button
                          onClick={() => setDeleteConfirmFor(m)}
                          title="Apagar para todos"
                          className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      )}
                    </div>
                  );

                  return (
                    <Fragment key={m.id}>
                      {dayDivider}
                      <div
                        className={`group flex items-end gap-1 min-w-0 ${
                          m.direction === "out" ? "justify-end" : ""
                        }`}
                      >
                      {m.direction === "out" && actions}
                      <div className="relative max-w-[62%] min-w-0">
                        {reactionPickerFor === m.id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setReactionPickerFor(null)}
                            ></div>
                            <div
                              className={`absolute -top-11 z-50 bg-white rounded-full drop-shadow-far px-2 py-1.5 flex gap-1.5 ${
                                m.direction === "out" ? "right-0" : "left-0"
                              }`}
                            >
                              {QUICK_REACTIONS.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => onReact(m, emoji)}
                                  className="text-base leading-none hover:scale-125 transition-transform"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </>
                        )}

                        <div
                          className={`text-xs drop-shadow break-words whitespace-pre-wrap ${
                            m.type === "image" || m.type === "video" ? "p-1.5" : "py-2.5 px-3.5"
                          } ${
                            m.direction === "out"
                              ? "bg-purple text-white rounded-[15px_4px_15px_15px]"
                              : "bg-white text-black rounded-[4px_15px_15px_15px]"
                          } ${reactionChips.length > 0 ? "mb-2.5" : ""}`}
                        >
                          {m.deleted ? (
                            <span
                              className={`italic flex items-center gap-1 ${
                                m.direction === "out" ? "text-white/70" : "text-gray-dark"
                              }`}
                            >
                              <FontAwesomeIcon icon={faBan} className="text-[10px]" />
                              Mensagem apagada
                            </span>
                          ) : (
                            <>
                              {m.direction === "out" && m.authorAgentId && (
                                <div className="font-bold text-[11px] mb-0.5 text-white/90">
                                  {extensions.find((e) => e.value === m.authorAgentId)?.name}
                                </div>
                              )}
                              {replied && (
                                <div
                                  className={`mb-1.5 border-l-2 rounded-5 px-2 py-1 text-[11px] truncate ${
                                    m.direction === "out"
                                      ? "border-white/60 bg-white/10 text-white/85"
                                      : "border-purple/60 bg-purple/5 text-gray-dark"
                                  }`}
                                >
                                  {replied.deleted
                                    ? "Mensagem apagada"
                                    : replied.type === "text" || replied.type === "unsupported"
                                      ? replied.text
                                      : `${
                                          replied.type === "image"
                                            ? "📷 Imagem"
                                            : replied.type === "video"
                                              ? "🎥 Vídeo"
                                              : replied.type === "audio"
                                                ? "🎧 Áudio"
                                                : "📄 " + (replied.fileName || "Documento")
                                        }`}
                                </div>
                              )}
                              {m.type === "image" && m.mediaUrl && (
                                <a href={m.mediaUrl} target="_blank" rel="noreferrer">
                                  <img
                                    src={m.mediaUrl}
                                    alt={m.fileName || "imagem"}
                                    className="rounded-10 max-w-full max-h-[260px] object-cover block"
                                  />
                                </a>
                              )}
                              {m.type === "video" && m.mediaUrl && (
                                <video
                                  controls
                                  src={m.mediaUrl}
                                  className="rounded-10 max-w-full max-h-[260px] block"
                                />
                              )}
                              {m.type === "document" && m.mediaUrl && (
                                <a
                                  href={m.mediaUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className={`flex items-center gap-2 min-w-0 ${
                                    m.direction === "out" ? "text-white" : "text-primary"
                                  }`}
                                >
                                  <span className="text-base flex-none">📄</span>
                                  <span className="underline truncate">
                                    {m.fileName || "documento"}
                                  </span>
                                </a>
                              )}
                              {m.type === "audio" && m.mediaUrl && (
                                <audio controls src={m.mediaUrl} className="max-w-[220px]" />
                              )}
                              {/* Legenda de mídia (bug real: chegava do backend em
                                  m.text, mas nunca era renderizada pra tipo != texto —
                                  corrigido 2026-09-25) */}
                              {m.text &&
                                (m.type === "image" ||
                                  m.type === "video" ||
                                  m.type === "document" ||
                                  m.type === "audio") && (
                                  <div className="mt-1.5">{m.text}</div>
                                )}
                              {(m.type === "text" || m.type === "unsupported") && m.text}
                            </>
                          )}
                          <div className="flex items-center justify-end gap-1 mt-0.5">
                            <span
                              className={`text-[10px] ${
                                m.direction === "out" ? "text-white/70" : "text-gray-dark"
                              }`}
                            >
                              {formatMessageTime(m.timestamp)}
                            </span>
                            {m.direction === "out" && <MessageTicks status={m.status} />}
                          </div>
                        </div>

                        {reactionChips.length > 0 && (
                          <div
                            className={`absolute -bottom-2.5 bg-white rounded-full px-1.5 py-0.5 text-[11px] drop-shadow flex gap-0.5 ${
                              m.direction === "out" ? "right-2" : "left-2"
                            }`}
                          >
                            {reactionChips.join(" ")}
                          </div>
                        )}
                      </div>
                      {m.direction === "in" && actions}
                      </div>
                    </Fragment>
                  );
                })}
              </div>

              {isUnassigned && (
                <div className="flex items-center justify-between gap-4 py-3.5 px-5 border-t border-white-dark bg-white-dark flex-none">
                  <p className="text-xs font-medium">
                    Conversa aguardando atendimento.{" "}
                    <b className="text-primary">Assuma</b> para poder responder.
                  </p>
                  <Button onClick={onAssume} className="!text-xs" isLoading={assuming}>
                    Assumir conversa
                  </Button>
                </div>
              )}

              {isOther && (
                <div className="py-3.5 px-5 border-t border-white-dark bg-white-dark text-center text-xs text-gray-dark font-medium flex-none">
                  Somente leitura — conversa atribuída a outro atendente.
                </div>
              )}

              {isMine && replyTo && (
                <div className="flex items-center justify-between gap-3 py-2 px-4 border-t border-white-dark bg-white-dark flex-none">
                  <div className="min-w-0 border-l-2 border-purple pl-2.5">
                    <div className="text-[10px] font-bold text-purple">
                      Respondendo{" "}
                      {replyTo.direction === "out" ? "sua mensagem" : "mensagem"}
                    </div>
                    <div className="text-[11px] text-gray-dark truncate max-w-[380px]">
                      {replyTo.type === "text" || replyTo.type === "unsupported"
                        ? replyTo.text
                        : replyTo.type === "image"
                          ? "📷 Imagem"
                          : replyTo.type === "video"
                            ? "🎥 Vídeo"
                            : replyTo.type === "audio"
                              ? "🎧 Áudio"
                              : "📄 " + (replyTo.fileName || "Documento")}
                    </div>
                  </div>
                  <button
                    onClick={() => setReplyTo(null)}
                    className="text-gray-dark hover:text-primary text-sm flex-none px-1"
                    title="Cancelar resposta"
                  >
                    ✕
                  </button>
                </div>
              )}

              {isMine && recording && (
                <div className="flex items-center gap-3 py-3.5 px-4 border-t border-white-dark flex-none">
                  <button
                    onClick={() => stopRecording(true)}
                    title="Cancelar gravação"
                    className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                  <span className="w-2 h-2 rounded-full bg-red animate-pulse flex-none"></span>
                  <span className="flex-1 text-xs font-bold text-primary tabular-nums">
                    {Math.floor(recordSeconds / 60)}:
                    {String(recordSeconds % 60).padStart(2, "0")}
                  </span>
                  <button
                    onClick={() => stopRecording(false)}
                    title="Enviar áudio"
                    className="w-9 h-9 rounded-full bg-purple text-white grid place-items-center flex-none text-sm"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>
              )}

              {isMine && !recording && (
                <div className="flex items-end gap-2.5 py-3.5 px-4 border-t border-white-dark flex-none relative">
                  <button
                    onClick={() => setEmojiOpen((v) => !v)}
                    className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm"
                    title="Emoji"
                  >
                    <FontAwesomeIcon icon={faSmile} />
                  </button>

                  {emojiOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setEmojiOpen(false)}
                      ></div>
                      <div className="absolute left-2 bottom-full mb-2 w-[280px] bg-white rounded-15 drop-shadow-far p-2.5 z-50 grid grid-cols-8 gap-1">
                        {EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => insertEmoji(emoji)}
                            className="text-lg leading-none py-1.5 rounded-10 hover:bg-white-dark grid place-items-center"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) onAttachFile(file);
                      e.target.value = "";
                    }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm disabled:opacity-50"
                    title="Anexar arquivo"
                  >
                    {uploading ? "…" : <FontAwesomeIcon icon={faPaperclip} />}
                  </button>

                  <button
                    onClick={startRecording}
                    disabled={uploading}
                    className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm disabled:opacity-50"
                    title="Gravar áudio"
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>

                  <textarea
                    ref={composerRef}
                    rows={1}
                    className="flex-1 min-w-0 bg-white-dark rounded-[20px] py-2.5 px-4 text-xs outline-none resize-none leading-normal max-h-[120px] overflow-y-auto"
                    placeholder="Escreva uma mensagem"
                    value={composerText}
                    onChange={(e) => setComposerText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        onSend();
                      }
                    }}
                    disabled={sending}
                  />
                  <button
                    onClick={onSend}
                    disabled={sending || !composerText.trim()}
                    className="w-9 h-9 rounded-full bg-purple text-white grid place-items-center flex-none text-sm disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </button>
                </div>
              )}
            </>
          )}
          </>
          )}
        </Card>
      </div>
      )}

      {groupsHook.showCreateModal && (
        <CreateGroupModal
          contacts={contacts}
          extensions={extensions}
          onClose={() => groupsHook.setShowCreateModal(false)}
          onCreated={(group) => {
            groupsHook.setShowCreateModal(false);
            groupsHook.setSelectedId(group.id);
          }}
        />
      )}

      {showNewConvModal && (
        <NewConversationModal
          contacts={contacts}
          queues={queues}
          isAdmin={isAdmin}
          onClose={() => setShowNewConvModal(false)}
          onCreated={(conversation) => {
            setShowNewConvModal(false);
            // startConversation reaproveita conversa aberta existente pro
            // mesmo telefone em vez de duplicar — se já estiver com outro
            // atendente, navegar pra ela não adianta nada (some da tela por
            // causa da regra de visibilidade), então só avisa.
            if (conversation.assignedAgentId && conversation.assignedAgentId !== myId) {
              const agentName =
                extensions.find((e) => e.value === conversation.assignedAgentId)
                  ?.name || "outro atendente";
              toast.info(`Esse contato já está em atendimento com ${agentName}.`);
              return;
            }
            setSelectedId(conversation.id);
            setTab("mine");
          }}
        />
      )}

      {showAddContactModal && selected && (
        <AddContactModal
          conversation={selected}
          onClose={() => setShowAddContactModal(false)}
          onCreated={() => setShowAddContactModal(false)}
        />
      )}

      {deleteConfirmFor && (
        <DeleteMessageConfirmModal
          onClose={() => setDeleteConfirmFor(null)}
          onConfirm={onDeleteMessage}
          deleting={deletingMessage}
        />
      )}
    </SidebarLayout>
  );
}

function NewConversationModal(props: {
  contacts: WhatsappContact[];
  queues: Queue[];
  isAdmin: boolean;
  onClose: () => void;
  onCreated: (conversation: WhatsappConversation) => void;
}) {
  const [subTab, setSubTab] = useState<"existing" | "new">("existing");
  const [contactSearch, setContactSearch] = useState("");
  const [contactId, setContactId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [sector, setSector] = useState("");
  const [queueId, setQueueId] = useState(props.queues[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);

  const filteredContacts = props.contacts.filter(
    (c) =>
      !contactSearch ||
      c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
      String(c.phone).includes(contactSearch),
  );

  const onDeleteContact = async (c: WhatsappContact) => {
    if (!window.confirm(`Excluir o contato "${c.name}" da agenda?`)) return;
    if (contactId === c.id) setContactId("");
    const result = await deleteContact(c.id);
    if (result.error) toast.error("Não foi possível excluir o contato.");
  };

  // `phone` guarda só DDD+número (máscara igual à agenda normal — sem DDI);
  // os contatos já salvos guardam com DDI (55). Compara pelas variantes
  // pra achar duplicata de verdade, mesmo com formatos diferentes.
  const duplicatePhone =
    !!phone.trim() &&
    (() => {
      const variants = new Set(phoneLookupVariants(phone.trim()));
      return props.contacts.some((c) => variants.has(c.phone));
    })();

  const valid =
    subTab === "existing"
      ? !!contactId
      : !!name.trim() && !!phone.trim() && !duplicatePhone;

  const onSubmit = async () => {
    if (!valid || !queueId || submitting) return;
    setSubmitting(true);
    const result = await startConversation(
      subTab === "existing"
        ? { queueId, contactId }
        : {
            queueId,
            newContact: {
              name: name.trim(),
              phone: phone.trim(),
              email: email.trim() || undefined,
              company: company.trim() || undefined,
              sector: sector.trim() || undefined,
            },
          },
    );
    setSubmitting(false);
    if (result.error) {
      toast.error("Não foi possível iniciar a conversa.");
      return;
    }
    props.onCreated(result.data);
  };

  return (
    <Modal shown title="Nova conversa" onClose={props.onClose} className="w-[420px]">
      <div className="flex gap-1.5 mb-4">
        <button
          onClick={() => setSubTab("existing")}
          className={`py-1.5 px-3 rounded-10 text-xs font-bold ${
            subTab === "existing" ? "bg-purple/10 text-purple" : "text-gray-dark"
          }`}
        >
          Contato existente
        </button>
        <button
          onClick={() => setSubTab("new")}
          className={`py-1.5 px-3 rounded-10 text-xs font-bold ${
            subTab === "new" ? "bg-purple/10 text-purple" : "text-gray-dark"
          }`}
        >
          Novo contato
        </button>
      </div>

      {subTab === "existing" && (
        <>
          <Field
            placeholder="Buscar por nome ou telefone"
            value={contactSearch}
            setter={setContactSearch}
            autocomplete
            className="mb-3"
          />
          <div className="max-h-[220px] overflow-y-auto mb-4">
            {filteredContacts.map((c) => (
              <div
                key={c.id}
                className={`flex items-center gap-1 rounded-10 mb-1 ${
                  contactId === c.id ? "bg-purple/10" : "hover:bg-white-dark"
                }`}
              >
                <button
                  onClick={() => setContactId(c.id)}
                  className="flex-1 min-w-0 text-left px-3 py-2.5"
                >
                  <div className="font-bold text-xs text-primary">{c.name}</div>
                  <div className="text-[11px] text-gray-dark">
                    {formatPhoneDisplay(c.phone)}
                    {(c.company || c.sector) &&
                      ` · ${[c.company, c.sector].filter(Boolean).join(" · ")}`}
                  </div>
                </button>
                {props.isAdmin && (
                  <button
                    onClick={() => onDeleteContact(c)}
                    title="Excluir contato"
                    className="flex-none w-7 h-7 mr-1 rounded-10 text-gray-dark hover:bg-red/10 hover:text-red grid place-items-center text-xs"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                )}
              </div>
            ))}
            {filteredContacts.length === 0 && (
              <div className="text-center text-xs text-gray-dark py-4">
                Nenhum contato encontrado na agenda.
              </div>
            )}
          </div>
        </>
      )}

      {subTab === "new" && (
        <>
          <Field title="Nome" value={name} setter={setName} className="mb-3" />
          <Field
            title="Telefone"
            placeholder="(11) 90000-0000"
            value={phone}
            setter={setPhone}
            mask={phoneMask}
            className="mb-1"
            error={duplicatePhone}
          />
          {duplicatePhone && (
            <div className="text-[10.5px] text-red font-semibold mb-2">
              Já existe um contato com esse telefone na agenda.
            </div>
          )}
          <div className="flex gap-3 mb-3">
            <Field title="Email" value={email} setter={setEmail} className="flex-1" />
            <Field
              title="Empresa"
              value={company}
              setter={setCompany}
              className="flex-1"
            />
          </div>
          <Field title="Setor" value={sector} setter={setSector} className="mb-3" />
        </>
      )}

      <div className="mb-2">
        <div className="text-[11px] font-bold text-gray-dark uppercase mb-1.5">Fila</div>
        <div className="flex flex-wrap gap-1.5">
          {props.queues.map((q) => (
            <button
              key={q.id}
              onClick={() => setQueueId(q.id)}
              className={`py-1.5 px-3 rounded-10 text-xs font-bold border-2 ${
                queueId === q.id
                  ? "border-purple text-purple bg-purple/10"
                  : "border-gray text-gray-dark"
              }`}
            >
              {q.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2.5 mt-5">
        <Button outline color="gray" onClick={props.onClose}>
          Cancelar
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!valid || !queueId || submitting}
          isLoading={submitting}
        >
          Iniciar conversa
        </Button>
      </div>
    </Modal>
  );
}

function AddContactModal(props: {
  conversation: WhatsappConversation;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState(props.conversation.contactName);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [sector, setSector] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const result = await createContactForConversation({
      conversationId: props.conversation.id,
      name: name.trim() || props.conversation.contactName,
      email: email.trim() || undefined,
      company: company.trim() || undefined,
      sector: sector.trim() || undefined,
    });
    setSubmitting(false);
    if (result.error) {
      toast.error("Não foi possível salvar o contato.");
      return;
    }
    props.onCreated();
  };

  return (
    <Modal
      shown
      title="Adicionar aos contatos"
      onClose={props.onClose}
      className="w-[360px]"
    >
      <Field title="Nome" value={name} setter={setName} className="mb-3" />
      <div className="mb-3">
        <div className="text-[11px] font-bold text-gray-dark uppercase mb-1.5">
          Telefone
        </div>
        <div className="bg-white-dark rounded-10 py-2 px-3 text-xs">
          {formatPhoneDisplay(props.conversation.contactPhone)}
        </div>
      </div>
      <div className="flex gap-3 mb-3">
        <Field title="Email" value={email} setter={setEmail} className="flex-1" />
        <Field title="Empresa" value={company} setter={setCompany} className="flex-1" />
      </div>
      <Field title="Setor" value={sector} setter={setSector} className="mb-3" />
      <div className="flex justify-end gap-2.5 mt-5">
        <Button outline color="gray" onClick={props.onClose}>
          Cancelar
        </Button>
        <Button onClick={onSubmit} disabled={submitting} isLoading={submitting}>
          Salvar contato
        </Button>
      </div>
    </Modal>
  );
}

// Input simples com label — TextInput exige formValidation obrigatório
// (registro num form maior), o que essas telas não têm.
function Field(props: {
  title?: string;
  placeholder?: string;
  value: string;
  setter: (v: string) => void;
  className?: string;
  error?: boolean;
  autocomplete?: boolean;
  // Mesmo formato de máscara do resto do Voki (utils/masks.js — phoneMask
  // etc.): função que devolve {filtering, masking}. `value`/`setter`
  // continuam guardando o dígito puro (sem DDI, mesmo padrão da agenda de
  // contatos "normal"); a máscara só formata o que aparece na tela.
  mask?: () => { filtering: (v: string) => string; masking: (v: string) => string };
}) {
  const { filtering, masking } = props.mask?.() || {};
  return (
    <div className={props.className}>
      {props.title && (
        <div className="text-[11px] font-bold text-gray-dark uppercase mb-1.5">
          {props.title}
        </div>
      )}
      <input
        className={`block bg-white w-full px-3 py-1.5 border-2 text-xs text-primary rounded-10 outline-none ${
          props.error ? "border-red/60" : "border-gray/50"
        }`}
        value={masking ? masking(props.value) : props.value}
        placeholder={props.placeholder}
        onChange={(e) =>
          props.setter(filtering ? filtering(e.target.value) : e.target.value)
        }
      />
    </div>
  );
}

export default WhatsappPage;
